import { ReactNode } from 'react';

export interface TableStatusBarAction {
    label?: string;
    icon?: ReactNode;
    onClick?: () => void;
    tone?: 'default' | 'primary' | 'success' | 'danger' | 'warning';
}

/**
 * Barre d'état standardisée pour les tables de l'application.
 *
 * Affiche :
 *  - Le nombre de résultats (ou un libellé personnalisé) à gauche.
 *  - Des actions contextuelles à droite (boutons, filtres, etc.).
 *
 * S'adapte au thème (clair/sombre) via les variables CSS.
 *
 * Exemple :
 *   <TableStatusBar count={dataTri.length} loading={loading} onRefresh={LoadData} />
 */
const TableStatusBar = (props: {
    count?: number;
    label?: string;
    loading?: boolean;
    actions?: TableStatusBarAction[];
    onRefresh?: () => void;
    selectedCount?: number;
}) => {
    const {
        count,
        label,
        loading,
        actions = [],
        onRefresh,
        selectedCount,
    } = props;

    const text = label ?? (count !== undefined ? `${count} résultat(s)` : '');

    return (
        <div className="eb-table-statusbar">
            <div className="eb-table-statusbar-left">
                {loading && <span className="eb-table-statusbar-spinner" aria-hidden />}
                <span className="eb-table-statusbar-text">{text}</span>
                {selectedCount !== undefined && selectedCount > 0 && (
                    <span className="eb-table-statusbar-badge">
                        {selectedCount} sélectionné(s)
                    </span>
                )}
            </div>
            <div className="eb-table-statusbar-right">
                {onRefresh && (
                    <button
                        className="eb-table-statusbar-btn"
                        onClick={onRefresh}
                        title="Actualiser"
                        disabled={loading}
                    >
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none"
                            stroke="currentColor" strokeWidth="2"
                            strokeLinecap="round" strokeLinejoin="round">
                            <path d="M23 4v6h-6" />
                            <path d="M1 20v-6h6" />
                            <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10" />
                            <path d="M20.49 15a9 9 0 0 1-14.85 3.36L1 14" />
                        </svg>
                        <span>Actualiser</span>
                    </button>
                )}
                {actions.map((a, i) => (
                    <button
                        key={i}
                        className={`eb-table-statusbar-btn tone-${a.tone || 'default'}`}
                        onClick={a.onClick}
                        title={a.label}
                    >
                        {a.icon}
                        {a.label && <span>{a.label}</span>}
                    </button>
                ))}
            </div>
        </div>
    );
};

export default TableStatusBar;
